import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FilePondOptions } from 'filepond';
import { FilePondComponent } from 'ngx-filepond';
import { Store } from '@ngrx/store';
import { State } from '../../shared/core/state';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import {
  ILiveClassEntity,
  LiveClassDTO,
  IMeetingEntity,
  LiveClassEntity,
  MeetingEntity,
} from '../../shared/core/state/live-class/models/live-class.dto';

import * as LiveClassActions from '../../shared/core/state/live-class/live-class.action';
import { Resource } from '../../shared/core/state/resource/models';
import { FilePondConfigService } from '../../shared/services/filepond-config.service';
import { ToastService } from '@black/ui';
import { environment } from '../../../environments/environment';
import {
  SUBJECT_OPTIONS_BY_AUDIENCE,
  TARGET_AUDIENCE_OPTIONS,
} from '../../shared/util/sri-lanka-data';

@Component({
  selector: 'black-add-live-class',
  templateUrl: './add-live-class.component.html',
  styleUrl: './add-live-class.component.css',
  standalone: false,
  host: {
    ngSkipHydration: 'true',
  },
})
export class AddLiveClassComponent implements OnInit {
  private fb = inject(NonNullableFormBuilder);
  private store = inject<Store<State>>(Store);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private filePondConfig = inject(FilePondConfigService);

  currentSection = 1;
  targetAudienceOptions = TARGET_AUDIENCE_OPTIONS;
  meetingSchedules: IMeetingEntity[] = [];
  editModeEnabled = false;
  isPublished = false;
  liveClassId: string | null = null;


  @ViewChild('myPond')
  myPond!: FilePondComponent;

  pondFiles: any[] = [];

  pondOptions: FilePondOptions = {
    allowMultiple: false,
    labelIdle: 'Drop files here or Browse',
    instantUpload: true,
    allowFileTypeValidation: true,
    acceptedFileTypes: ['image/png', 'image/jpeg'],
  };

  createLiveClassForm = this.fb.group({
    basic: this.fb.group({
      title: ['', Validators.required],
      description: ['', Validators.required],
      summary: ['', Validators.required],
    }),
    information: this.fb.group({
      startDate: ['', Validators.required],
      endDate: ['', Validators.required],
      medium: ['TAMIL', Validators.required],
      classMode: ['ONLINE', Validators.required],
      targetAudience: ['', Validators.required],
      targetAudienceOther: [''],
      subject: ['', Validators.required],
      subjectOther: [''],
      requirements: this.fb.array([this.fb.control('')]),
      coverage: this.fb.array([this.fb.control('')]),
      tags: [''],
    }),
    settings: this.fb.group({
      maxStudents: [100, [Validators.required, Validators.min(1)]],
      isPublic: [true],
      price: [0],
      banner: ['', Validators.required],
    }),
    socialLinks: this.fb.group({
      whatsapp: [''],
      telegram: [''],
    }),
    meetingsLength: this.fb.control(0, Validators.min(1)),
  });

  formControls = {
    basic: this.createLiveClassForm.controls.basic.controls,
    information: this.createLiveClassForm.controls.information.controls,
    settings: this.createLiveClassForm.controls.settings.controls,
    socialLinks: this.createLiveClassForm.controls.socialLinks.controls,
  };

  async ngOnInit(): Promise<void> {
    const server = await this.filePondConfig.getServerConfig({ allowRevert: true });
    this.pondOptions = { ...this.pondOptions, server };

    this.store.dispatch(LiveClassActions.initiateLiveClassCreation());

    // Add/remove required validator on targetAudienceOther based on targetAudience selection
    this.createLiveClassForm.controls.information.controls.targetAudience.valueChanges.subscribe(
      (value) => {
        const otherControl =
          this.createLiveClassForm.controls.information.controls
            .targetAudienceOther;
        if (value === 'Other') {
          otherControl.setValidators([Validators.required]);
        } else {
          otherControl.clearValidators();
        }
        otherControl.updateValueAndValidity();

        // Reset subject when audience changes
        this.createLiveClassForm.controls.information.controls.subject.setValue(
          '',
        );
        this.createLiveClassForm.controls.information.controls.subjectOther.setValue(
          '',
        );
        this.createLiveClassForm.controls.information.controls.subjectOther.clearValidators();
        this.createLiveClassForm.controls.information.controls.subjectOther.updateValueAndValidity();
      },
    );

    // Add/remove required validator on subjectOther based on subject selection
    this.createLiveClassForm.controls.information.controls.subject.valueChanges.subscribe(
      (value) => {
        const subjectOtherControl =
          this.createLiveClassForm.controls.information.controls.subjectOther;
        if (value === 'Other') {
          subjectOtherControl.setValidators([Validators.required]);
        } else {
          subjectOtherControl.clearValidators();
        }
        subjectOtherControl.updateValueAndValidity();
      },
    );

    this.liveClassId = this.route.snapshot.paramMap.get('id');

    if (this.liveClassId && this.route.snapshot.data['editMode']) {
      this.store.dispatch(
        LiveClassActions.restoreLiveClassItem({ id: this.liveClassId }),
      );
    }

    this.store
      .select((state) => state.liveClass.createdLiveClassItem)
      .subscribe((liveClassItem) => {
        if (!liveClassItem) {
          return;
        }

        this.editModeEnabled = true;
        this.isPublished = liveClassItem.status === 'PUBLISHED';

        const liveClassFormData = new LiveClassEntity(liveClassItem);

        this.patchFormControls(liveClassFormData);

        this.createLiveClassForm.setValue(liveClassFormData);

        if (this.isPublished) {
          this.formControls.information.startDate.disable();
          this.formControls.information.endDate.disable();
          this.formControls.settings.price.disable();
        }

        const bannerResource = liveClassItem.settings
          .bannerImageResource as Resource;

        this.pondFiles.push({
          source: bannerResource._id,
          options: {
            type: 'limbo',
            file: {
              name: bannerResource.name,
              size: 3001025,
              type: 'image/png',
            },
          },
        });

        // Add Mettings Schedules
        this.meetingSchedules = [];
        liveClassItem.meetings.forEach((item) => {
          const itemEntity = new MeetingEntity(item);
          this.meetingSchedules.push(itemEntity);
        });
      });
  }

  patchFormControls(formData: LiveClassEntity) {
    const requirementFormControlLength =
      this.formControls.information.requirements.controls.length;
    const coverageFormControlLength =
      this.formControls.information.coverage.controls.length;

    const requirementFormLength = formData.information.requirements.length;
    const coverageFormLength = formData.information.coverage.length;

    for (
      let index = 0;
      index < requirementFormLength - requirementFormControlLength;
      index++
    ) {
      this.formControls.information.requirements.push(this.fb.control(''));
    }

    for (
      let index = 0;
      index < coverageFormLength - coverageFormControlLength;
      index++
    ) {
      this.formControls.information.coverage.push(this.fb.control(''));
    }

    if (coverageFormLength < coverageFormControlLength) {
      const diff = coverageFormControlLength - coverageFormLength;

      for (let index = 0; index < diff; index++) {
        this.formControls.information.coverage.controls.pop();
      }
    }

    if (requirementFormLength < requirementFormControlLength) {
      const diff = requirementFormControlLength - requirementFormLength;

      for (let index = 0; index < diff; index++) {
        this.formControls.information.requirements.controls.pop();
      }
    }
  }

  submit() {
    this.createLiveClassForm.markAllAsTouched();

    if (this.createLiveClassForm.valid) {
      const liveClassFormEntity = this.createLiveClassForm
        .getRawValue() as ILiveClassEntity;
      const liveClassDTO = new LiveClassDTO(
        liveClassFormEntity,
        this.meetingSchedules,
      );

      if (this.editModeEnabled && this.liveClassId) {
        this.store.dispatch(
          LiveClassActions.updateLiveClass({
            liveClassId: this.liveClassId,
            liveClass: liveClassDTO,
            redirectTo: 'live-classes/add-success/' + this.liveClassId,
          }),
        );
      } else {
        this.store.dispatch(
          LiveClassActions.createLiveClass({ liveClassDTO: liveClassDTO }),
        );
      }
    } else {
      this.toastService.showToast(
        'Please fill all the required fields',
        'error',
      );
    }
  }

  numberArrays(from: number, to: number) {
    const numArr = [];
    for (let index = from - 1; index < to; index++) {
      numArr.push(`${index + 1}`);
    }
    return numArr;
  }

  pondHandleProcessFile(event: any) {
    if (!event.error) {
      this.createLiveClassForm.controls.settings.controls.banner.setValue(
        event.file.serverId,
      );
    }
  }

  pondHandleRevertFile(event: any) {
    this.createLiveClassForm.controls.settings.controls.banner.setValue('');
  }

  increaseCount() {
    const maxStudentsControl = this.formControls.settings.maxStudents;
    maxStudentsControl.setValue(
      maxStudentsControl.value != null ? maxStudentsControl.value + 1 : 0,
    );
  }

  decreaseCount() {
    const maxStudentsControl = this.formControls.settings.maxStudents;
    maxStudentsControl.setValue(
      maxStudentsControl.value ? maxStudentsControl.value - 1 : 0,
    );
  }

  get requirementsFormControls() {
    return this.createLiveClassForm.controls.information.controls.requirements
      .controls;
  }

  get coverageFormControls() {
    return this.formControls.information.coverage.controls;
  }

  get subjectOptions(): string[] {
    const audience = this.formControls.information.targetAudience.value;
    return SUBJECT_OPTIONS_BY_AUDIENCE[audience] ?? [];
  }

  get calculatedMaxDuration(): number {
    if (!this.meetingSchedules.length) return 0;
    return Math.max(
      ...this.meetingSchedules.map((e) => {
        const [sh, sm] = e.from.split(':').map(Number);
        const [eh, em] = e.to.split(':').map(Number);
        let diff = eh * 60 + em - (sh * 60 + sm);
        if (diff < 0) diff += 24 * 60;
        return diff;
      }),
    );
  }

  formatDuration(minutes: number): string {
    if (minutes === 0) return 'Not set';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h === 0) return `${m} min`;
    if (m === 0) return `${h} hr`;
    return `${h} hr ${m} min`;
  }


  addMoreRequirementInputs() {
    const requirementsFormArray =
      this.createLiveClassForm.controls.information.controls.requirements;

    requirementsFormArray.push(this.fb.control(''));
  }

  addMoreCoverageInputs() {
    const coverageFormArray =
      this.createLiveClassForm.controls.information.controls.coverage;

    coverageFormArray.push(this.fb.control(''));
  }

  syncMeetingsLength = () => {
    this.createLiveClassForm.controls.meetingsLength.setValue(
      this.meetingSchedules.length,
    );
  };
}
